// Shared primitives for the second-wave reinsurance screens.
//
// Styles, money and date formatting, and the status pill. One definition each
// — the first wave grew a per-screen copy of the pill and of fmtMoney, and
// they have already drifted (two of them round to whole units, one does not).

import React from 'react';
import { formatDate } from '../regureStyles';

export const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// ─── Money ────────────────────────────────────────────────────
//
// The number of decimal places is a property of the CURRENCY, not a display
// preference. BHD, KWD and OMR are three-decimal currencies: rendering
// "BHD 1,234.50" understates a balance by a factor of ten in the last place.
// This mirrors the backend's single table in services/genericOfferDocument.js
// — it cannot be imported across the wire, so it is kept deliberately short
// and identical.
export const CURRENCY_DECIMALS = {
  BHD: 3, KWD: 3, OMR: 3, JOD: 3, TND: 3, LYD: 3, IQD: 3,
  JPY: 0, KRW: 0, CLP: 0, ISK: 0, VND: 0,
};

const decimalsFor = (ccy) => CURRENCY_DECIMALS[String(ccy || '').toUpperCase()] ?? 2;

/** Exact money, with the currency's own minor units. */
export const fmtMoney = (n, ccy) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  const dp = decimalsFor(ccy);
  const amt = Number(n).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  return ccy ? `${ccy} ${amt}` : amt;
};

/**
 * Abbreviated money for headline figures — "USD 42.8M", as in the design.
 * Only for display where the exact figure is available elsewhere; never for a
 * balance someone is expected to reconcile against.
 */
export const fmtMoneyShort = (n, ccy) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const abs = Math.abs(v);
  const unit = abs >= 1e9 ? ['B', 1e9] : abs >= 1e6 ? ['M', 1e6] : abs >= 1e3 ? ['K', 1e3] : null;
  if (!unit) return fmtMoney(v, ccy);
  const scaled = (v / unit[1]).toFixed(1).replace(/\.0$/, '');
  return `${ccy ? `${ccy} ` : ''}${scaled}${unit[0]}`;
};

/**
 * A figure the API could not express in one currency. The endpoints return
 * `amount: null` plus the currencies involved rather than adding dinars to
 * dollars; this renders that honestly instead of showing "—" as if there
 * were no data.
 */
export const fmtMixed = (money) => {
  if (!money) return '—';
  if (money.amount !== null && money.amount !== undefined) {
    return fmtMoneyShort(money.amount, money.currency);
  }
  const list = money.currencies || [];
  // Only a genuinely MIXED scope gets the "n currencies" answer. One
  // currency and no amount is not a scope this cannot sum - it is missing
  // data, and saying "1 currencies" reported a field-name mismatch as if it
  // were a considered refusal to add dinars to dollars. That is exactly how
  // the Recoveries Due card read for a fortnight.
  if (list.length < 2) return '—';
  return `${list.length} currencies`;
};

export const fmtPercent = (n, dp = 0) =>
  (n === null || n === undefined || Number.isNaN(Number(n)) ? '—' : `${Number(n).toFixed(dp)}%`);

// Same fix as kit.js `date` - see the note there. Rendered every date a day
// early for viewers west of Greenwich and ignored the tenant's date format.
export const fmtDate = (d) => (d ? formatDate(d) : '—');

export const fmtDateISO = (d) => {
  if (!d) return '';
  try { return new Date(d).toISOString().slice(0, 10); } catch { return ''; }
};

// ─── Palette ──────────────────────────────────────────────────
export const C = {
  burgundy: '#800020',
  ink:      '#1e293b',
  body:     '#334155',
  muted:    '#64748b',
  faint:    '#94a3b8',
  border:   '#e2e8f0',
  hair:     '#f1f5f9',
  panel:    '#f8fafc',
  white:    '#ffffff',
  green:    '#16a34a',
  red:      '#dc2626',
  bar:      '#8da2c0',
  barTrack: '#eef2f7',
};

export const S = {
  page:    { flex: 1, overflowY: 'auto', padding: '24px 32px 48px', minHeight: 0, background: '#fbfcfd' },
  h1:      { fontSize: 25, fontWeight: 700, color: C.ink, margin: 0, letterSpacing: '-0.01em' },
  sub:     { color: C.muted, margin: '4px 0 0', fontSize: 13.5 },
  card:    { background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 },
  cardHead:{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  cardTitle:{ fontSize: 15.5, fontWeight: 700, color: C.ink, margin: 0 },
  link:    { color: '#475569', fontSize: 12.5, fontWeight: 600, textDecoration: 'none', cursor: 'pointer', background: 'none', border: 'none', padding: 0 },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:      { textAlign: 'left', padding: '9px 10px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  td:      { padding: '12px 10px', borderBottom: `1px solid ${C.hair}`, color: C.body, verticalAlign: 'middle' },
  num:     { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  btn:     { padding: '9px 16px', borderRadius: 10, border: 'none', background: C.burgundy, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  ghost:   { padding: '8px 14px', borderRadius: 9, border: `1px solid ${C.border}`, background: '#fff', color: C.body, cursor: 'pointer', fontWeight: 600, fontSize: 12.5 },
  select:  { padding: '8px 10px', borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff', color: C.body },
  input:   { padding: '8px 10px', borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff', width: 200 },
  empty:   { padding: '40px 20px', textAlign: 'center', color: C.muted, fontSize: 13 },
  err:     { color: C.red, fontSize: 13, padding: '10px 0' },
  toolbar: { display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' },
};

// ─── Status pill ──────────────────────────────────────────────
// One vocabulary across every second-wave screen. A status with no entry
// renders neutral rather than falling back to a colour that means something
// else — a grey "unknown" is honest, a green one is a lie.
const TONES = {
  green:   { bg: '#dcfce7', fg: '#166534' },
  blue:    { bg: '#dbeafe', fg: '#1e40af' },
  amber:   { bg: '#fef3c7', fg: '#92400e' },
  red:     { bg: '#fee2e2', fg: '#991b1b' },
  neutral: { bg: '#eef2f7', fg: '#475569' },
};

const STATUS_TONE = {
  // Placement pipeline
  submission: 'neutral', marketed: 'neutral', quoted: 'blue', bound: 'green',
  closing: 'amber', settled: 'green', 'in market': 'amber',
  // Treaty
  draft: 'neutral', placed: 'blue', expired: 'neutral', run_off: 'amber',
  // Accounts
  issued: 'blue', agreed: 'green', disputed: 'red',
  // Recoveries
  notified: 'neutral', invoiced: 'blue', received: 'green', written_off: 'neutral',
  // Claims
  validated: 'green', in_review: 'amber', 'in review': 'amber', pending: 'neutral',
  // Renewals
  upcoming: 'neutral', in_negotiation: 'amber', renewed: 'green', lapsed: 'red',
  replaced: 'blue', declined: 'red',
  // Compliance
  clear: 'green', flagged: 'red', blocked: 'red', complete: 'green',
  incomplete: 'amber', expired_kyc: 'red', not_screened: 'neutral',
};

export const humanise = (s) =>
  String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export function Pill({ status, label, tone }) {
  const key = String(status || '').toLowerCase();
  const t = TONES[tone || STATUS_TONE[key] || 'neutral'];
  return (
    <span style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: 999,
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      background: t.bg, color: t.fg,
    }}>
      {label || humanise(status) || '—'}
    </span>
  );
}

// ─── Trend chip ───────────────────────────────────────────────
// A null trend renders as nothing, not as 0%. "No comparison available" and
// "flat" are different facts and the dashboard must not conflate them.
export function Trend({ value, suffix = 'vs. previous period' }) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return <div style={{ fontSize: 11.5, color: C.faint, marginTop: 6 }}>no prior period</div>;
  }
  const up = Number(value) >= 0;
  return (
    <div style={{ marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ color: up ? C.green : C.red, fontSize: 12.5, fontWeight: 700 }}>
        {up ? '▲' : '▼'} {up ? '+' : ''}{Number(value).toFixed(0)}%
      </span>
      <span style={{ color: C.faint, fontSize: 11.5 }}>{suffix}</span>
    </div>
  );
}

// ─── Progress bar ─────────────────────────────────────────────
export function Bar({ percent, width = 92, tone = C.bar }) {
  const p = Math.max(0, Math.min(100, Number(percent) || 0));
  return (
    <span style={{
      display: 'inline-block', width, height: 7, borderRadius: 999,
      background: C.barTrack, overflow: 'hidden', verticalAlign: 'middle',
    }}>
      <span style={{ display: 'block', width: `${p}%`, height: '100%', background: tone, borderRadius: 999 }} />
    </span>
  );
}

// ─── Panel scaffolding ────────────────────────────────────────
export function Card({ title, action, children, style }) {
  return (
    <section style={{ ...S.card, ...style }}>
      {(title || action) && (
        <div style={S.cardHead}>
          {title ? <h2 style={S.cardTitle}>{title}</h2> : <span />}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function EmptyRow({ colSpan, children }) {
  return (
    <tr>
      <td colSpan={colSpan} style={{ ...S.td, ...S.empty, borderBottom: 'none' }}>{children}</td>
    </tr>
  );
}

// A table that scrolls inside its own container, so a wide row never makes
// the page scroll sideways.
export function ScrollX({ children }) {
  return <div style={{ overflowX: 'auto', margin: '0 -4px', padding: '0 4px' }}>{children}</div>;
}
